import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  BedDouble,
  Search,
  X,
  ImagePlus,
  GripVertical,
  Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roomTypes = ['standard', 'deluxe', 'executive', 'premium', 'suite', 'presidential'];

const defaultRoom = {
  name: '',
  type: 'deluxe',
  description: '',
  long_description: '',
  price: 0,
  size: '',
  features: { beds: 1, bath: 1, guests: 2 },
  amenities: [],
  images: []
};

export default function AdminRooms() {
  const { token } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState(defaultRoom);
  const [amenityInput, setAmenityInput] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms`);
      setRooms(response.data);
    } catch (error) {
      toast.error('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        images: formData.images.filter(img => img.trim() !== '')
      };

      if (editingRoom) {
        await axios.put(`${API_URL}/api/rooms/${editingRoom.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Room updated successfully');
      } else {
        await axios.post(`${API_URL}/api/rooms`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Room created successfully');
      }
      
      setIsDialogOpen(false);
      setEditingRoom(null);
      setFormData(defaultRoom);
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save room');
    }
  };

  const handleEdit = (room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      type: room.type,
      description: room.description,
      long_description: room.long_description || '',
      price: room.price,
      size: room.size,
      features: room.features,
      amenities: room.amenities,
      images: room.images || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room deleted successfully');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const addAmenity = () => {
    if (amenityInput.trim()) {
      setFormData({
        ...formData,
        amenities: [...formData.amenities, amenityInput.trim()]
      });
      setAmenityInput('');
    }
  };

  const removeAmenity = (index) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter((_, i) => i !== index)
    });
  };

  // Image management functions
  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData({
        ...formData,
        images: [...formData.images, newImageUrl.trim()]
      });
      setNewImageUrl('');
      toast.success('Image added');
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.images.length) return;
    const newImages = [...formData.images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setFormData({ ...formData, images: newImages });
  };

  const setAsMainImage = (index) => {
    if (index === 0) return;
    const newImages = [...formData.images];
    const [movedImage] = newImages.splice(index, 1);
    newImages.unshift(movedImage);
    setFormData({ ...formData, images: newImages });
    toast.success('Set as main image');
  };

  const filteredRooms = rooms.filter(room =>
    room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    room.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-rooms-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Rooms Management</h1>
          <p className="text-muted-foreground mt-1">{rooms.length} rooms total</p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingRoom(null);
            setFormData(defaultRoom);
            setNewImageUrl('');
          }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-emerald" data-testid="add-room-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Room
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRoom ? 'Edit Room' : 'Add New Room'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Room Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="room-name-input"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map(type => (
                        <SelectItem key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (per night)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseInt(e.target.value) || 0})}
                    required
                  />
                </div>
                <div>
                  <Label>Size (e.g., 350 sq ft)</Label>
                  <Input
                    value={formData.size}
                    onChange={(e) => setFormData({...formData, size: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Short Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Long Description</Label>
                <Textarea
                  value={formData.long_description}
                  onChange={(e) => setFormData({...formData, long_description: e.target.value})}
                  className="min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Beds</Label>
                  <Input
                    type="number"
                    value={formData.features.beds}
                    onChange={(e) => setFormData({
                      ...formData,
                      features: {...formData.features, beds: parseInt(e.target.value) || 1}
                    })}
                  />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input
                    type="number"
                    value={formData.features.bath}
                    onChange={(e) => setFormData({
                      ...formData,
                      features: {...formData.features, bath: parseInt(e.target.value) || 1}
                    })}
                  />
                </div>
                <div>
                  <Label>Max Guests</Label>
                  <Input
                    type="number"
                    value={formData.features.guests}
                    onChange={(e) => setFormData({
                      ...formData,
                      features: {...formData.features, guests: parseInt(e.target.value) || 1}
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Amenities</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={amenityInput}
                    onChange={(e) => setAmenityInput(e.target.value)}
                    placeholder="Add amenity (e.g., Free Wi-Fi, Air Conditioning)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                  />
                  <Button type="button" variant="outline" onClick={addAmenity}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.amenities.map((amenity, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {amenity}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeAmenity(idx)} />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Enhanced Image Management Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Room Images ({formData.images.length})</Label>
                  <p className="text-xs text-muted-foreground">First image is the main display image</p>
                </div>
                
                {/* Add new image */}
                <div className="flex gap-2">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Paste image URL here..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
                  />
                  <Button type="button" variant="outline" onClick={addImage} className="flex-shrink-0">
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Add Image
                  </Button>
                </div>

                {/* Image grid */}
                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                    {formData.images.map((img, idx) => (
                      <div 
                        key={idx} 
                        className={`relative group rounded-lg overflow-hidden border-2 ${idx === 0 ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-border'}`}
                      >
                        <div className="aspect-video bg-muted">
                          <img
                            src={img}
                            alt={`Room image ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/images/placeholder.jpg';
                              e.target.onerror = null;
                            }}
                          />
                        </div>
                        
                        {/* Image number badge */}
                        <div className="absolute top-2 left-2">
                          <Badge className={idx === 0 ? 'bg-emerald-600' : 'bg-black/60'}>
                            {idx === 0 ? 'Main' : `#${idx + 1}`}
                          </Badge>
                        </div>

                        {/* Action buttons overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {idx !== 0 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => setAsMainImage(idx)}
                              title="Set as main image"
                            >
                              Set Main
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setPreviewImage(img)}
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => removeImage(idx)}
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Reorder buttons */}
                        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {idx > 0 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="secondary"
                              className="w-6 h-6"
                              onClick={() => moveImage(idx, idx - 1)}
                              title="Move left"
                            >
                              ←
                            </Button>
                          )}
                          {idx < formData.images.length - 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="secondary"
                              className="w-6 h-6"
                              onClick={() => moveImage(idx, idx + 1)}
                              title="Move right"
                            >
                              →
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                    <ImagePlus className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No images added yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Add image URLs above to showcase this room</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-emerald" data-testid="save-room-btn">
                  {editingRoom ? 'Update Room' : 'Create Room'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Image Preview</DialogTitle>
            </DialogHeader>
            <div className="relative">
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search rooms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid gap-4">
        {filteredRooms.map((room, index) => (
          <motion.div
            key={room.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
          >
            <Card data-testid={`room-card-${room.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Room Images Preview */}
                  <div className="w-full md:w-48 flex-shrink-0">
                    <div className="relative h-32 rounded-lg overflow-hidden">
                      <img
                        src={room.images[0] || '/images/placeholder.jpg'}
                        alt={room.name}
                        className="w-full h-full object-cover"
                      />
                      {room.images.length > 1 && (
                        <Badge className="absolute bottom-2 right-2 bg-black/70">
                          +{room.images.length - 1} more
                        </Badge>
                      )}
                    </div>
                    {/* Thumbnail strip for multiple images */}
                    {room.images.length > 1 && (
                      <div className="flex gap-1 mt-1">
                        {room.images.slice(1, 4).map((img, imgIdx) => (
                          <div key={imgIdx} className="w-10 h-8 rounded overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {room.images.length > 4 && (
                          <div className="w-10 h-8 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                            +{room.images.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-serif text-lg text-foreground">{room.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="capitalize">{room.type}</Badge>
                          <span className="text-muted-foreground text-sm">{room.size}</span>
                          <span className="text-muted-foreground text-sm">• {room.images.length} images</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-xl text-foreground">₹{room.price.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">per night</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{room.description}</p>
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-sm text-muted-foreground">{room.features.beds} Bed • {room.features.bath} Bath • {room.features.guests} Guests</span>
                      <div className="flex-1" />
                      <Button variant="outline" size="sm" onClick={() => handleEdit(room)} data-testid={`edit-room-${room.id}`}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(room.id)} data-testid={`delete-room-${room.id}`}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {filteredRooms.length === 0 && (
          <div className="text-center py-12">
            <BedDouble className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No rooms found</p>
          </div>
        )}
      </div>
    </div>
  );
}
